import { z } from 'zod';
import { UPLOAD_CONTENT_TYPES } from './upload.schemas';

/** Photos only: proof of delivery and damage shots. Documents go through the office upload flow. */
export const SYNC_MEDIA_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const satisfies readonly (typeof UPLOAD_CONTENT_TYPES)[number][];
export const MAX_SYNC_MEDIA_BYTES = 10 * 1024 * 1024;

/** `POST /sync/media/presign`. */
export const syncMediaPresignRequestSchema = z.object({
  tripId: z.uuid(),
  contentType: z.enum(SYNC_MEDIA_CONTENT_TYPES),
  sizeBytes: z.number().int().positive().max(MAX_SYNC_MEDIA_BYTES),
});
export type SyncMediaPresignRequest = z.infer<typeof syncMediaPresignRequestSchema>;

/** A presigned POST: send the file as multipart form data to `url`, `fields` first. */
export const syncMediaPresignResponseSchema = z.object({
  objectKey: z.string(),
  url: z.string(),
  fields: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime(),
});
export type SyncMediaPresignResponse = z.infer<typeof syncMediaPresignResponseSchema>;

/** `POST /sync/media/confirm`: the upload to `objectKey` has finished. The server checks storage rather than trusting this. */
export const syncMediaConfirmRequestSchema = z.object({
  tripId: z.uuid(),
  objectKey: z.string().min(1).max(512),
  /** The phone's own id for this photo, the one its event's `mediaIds` names. Confirming it twice returns one row even under a new `objectKey`. */
  clientMediaId: z.uuid().optional(),
});
export type SyncMediaConfirmRequest = z.infer<typeof syncMediaConfirmRequestSchema>;

export const syncMediaConfirmResponseSchema = z.object({
  mediaId: z.uuid(),
  objectKey: z.string(),
  sizeBytes: z.number().int(),
  /** True when this object (or this `clientMediaId`) was confirmed before, under another idempotency key. */
  alreadyConfirmed: z.boolean(),
});
export type SyncMediaConfirmResponse = z.infer<typeof syncMediaConfirmResponseSchema>;
